import type {
  LocationQuery,
  LocationQueryRaw,
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationRaw,
} from "vue-router";
import type { AuthQueryParams } from "../types";
import { providerNameToQueryParam, queryParamToProviderName } from "../utils/providers";

export function useAuthQueryParams(to?: RouteLocationNormalizedLoadedGeneric) {
  const queryParamsSnapshot = ref<LocationQuery>({});

  const config = useRuntimeConfig();
  const router = useRouter();

  const authStore = useAuthStore();
  const localePath = useLocalePath();
  const getRouteBaseName = useRouteBaseName();

  // Do not use useRoute here, since it will be used in a middleware
  const currentRoute = computed(() => to ?? router.currentRoute.value);
  const currentRouteName = computed(() => getRouteBaseName(currentRoute.value));

  const isCurrentRouteHome = computed(() => currentRouteName.value === "index");
  const isCurrentRouteLogin = computed(() => currentRouteName.value === "login");

  const ticketQueryParam = computed(() => {
    const value = currentRoute.value.query.ticket;
    return value?.toString();
  });

  const redirectQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.redirect ?? currentRoute.value.query.redirect;
    return value?.toString();
  });

  /** if the page should open the login automatically */
  const autoLoginQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.autoLogin ?? currentRoute.value.query.autoLogin ?? "";
    return ["true", "1"].includes(value.toString().toLocaleLowerCase());
  });

  const loginHintQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.loginHint ?? currentRoute.value.query.loginHint;
    return value?.toString();
  });

  const providerNameQueryParam = computed(() => {
    const value = queryParamsSnapshot.value.provider ?? currentRoute.value.query.provider ?? "";
    return queryParamToProviderName(value.toString());
  });

  const tokenQueryParam = computed(() => currentRoute.value.query.token);

  const currentQueryParams = computed(() => ({
    redirect: redirectQueryParam.value,
    autoLogin: autoLoginQueryParam.value ? "true" : undefined,
    loginHint: loginHintQueryParam.value,
    provider: providerNameQueryParam.value,
    token: tokenQueryParam.value,
  }));

  const makeQueryParams = (options: AuthQueryParams = {}) => {
    return {
      ...currentQueryParams.value,
      redirect: options.redirect,
      autoLogin: options.autoLogin ? "true" : undefined,
      loginHint: options.loginHint,
      provider: providerNameToQueryParam(options.provider),
    };
  };

  const signupRoute = computed<RouteLocationRaw>(() => ({
    path: localePath({
      name: "signup",
    }),
    query: makeQueryParams(),
  }));

  const initQueryParams = async () => {
    // Initialize the query params snapshot with the current route's query parameters
    const query = {
      ...currentRoute.value.query, // Keeps existing query params
    };
    if (query.ticket?.length) {
      devConsole.info("🔑 ckAuth: removing ticket param", query.ticket);
      delete query.ticket;
    }
    queryParamsSnapshot.value = query;

    if (Object.keys(queryParamsSnapshot.value).length > 0) {
      devConsole.log(
        "🛠️ Auth page initialized with query params:",
        currentRoute.value.fullPath,
        unref(queryParamsSnapshot.value),
      );
    }
  };

  const navigateToPage = (path: string, query?: LocationQueryRaw) => {
    if (path === currentRoute.value.path) {
      devConsole.error(`🔒 ckAuth: Already on the page: ${path}`);
      return;
    }
    devConsole.info(`🔒 ckAuth: Redirecting to ${path} page from ${currentRoute.value.fullPath}`);

    return navigateTo({
      path: path,
      query,
    });
  };

  const goToAuthorizedPage = (path?: string) => {
    if (!authStore.isAuthed) {
      devConsole.warn(`🔒 ckAuth: User is not authenticated, not redirecting to authorized page ${path}`);
      goToUnauthorizedRedirectPage();
      return;
    }

    const targetPath = localePath(path ?? redirectQueryParam.value ?? "/");
    devConsole.info(`🔒 ckAuth: Redirecting to ${targetPath} from strict route`, currentRoute.value.fullPath);
    return navigateToPage(targetPath);
  };

  const goToUnauthorizedRedirectPage = (path?: string, options?: AuthQueryParams) => {
    const query = makeQueryParams(options);
    if (authStore.isAuthed) {
      devConsole.warn(`🔒 ckAuth: User is authenticated, not redirecting to unauthorized page ${path}`);
      return goToAuthorizedPage();
    }

    const unauthorizedRedirect = localePath(path || String(config.public.UNAUTHORIZED_REDIRECT || "/login"));
    if (unauthorizedRedirect === currentRoute.value.path) {
      devConsole.error(`🔒 ckAuth: Already on the unauthorized page: ${unauthorizedRedirect}`);
      return;
    }

    return navigateToPage(unauthorizedRedirect, query);
  };

  const goHome = () => {
    return goToAuthorizedPage("/");
  };

  const goBack = (path?: string) => {
    return navigateToPage(localePath(path ?? "/"));
  };

  const goToExternalApp = (appUrl: string | URL, ticketId?: string) => {
    const url = appUrl instanceof URL ? appUrl : new URL(appUrl);
    if (ticketId) {
      url.searchParams.set("ticket", ticketId);
    }

    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return {
    isCurrentRouteLogin,
    isCurrentRouteHome,
    signupRoute,
    autoLoginQueryParam,
    providerNameQueryParam,
    ticketQueryParam,
    initQueryParams,
    makeQueryParams,
    goToUnauthorizedRedirectPage,
    goToAuthorizedPage,
    goHome,
    goBack,
    goToExternalApp,
  };
}
